import * as React from "react";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import { SetState } from "source/app/services/UI/State/SetState";
import { JournalCaptureTextArea } from "../../Shared/Form/TextArea/JournalCaptureTextArea";
import { Style, StyleClass } from "../../Shared/Styles/CSS";
import { IQuickJournalState } from "./Data/QuickJournalTypes";
import { useQuickJournalDataController } from "./Hooks/useQuickJournalDataController";
import { useQuickJournalViewportController } from "./Hooks/useQuickJournalViewportController";
import { IsIosDevice } from "./View/QuickJournalLayout";
import {
    GetQuickJournalTheme,
    LoadStoredQuickJournalThemeName,
    quickJournalThemeStorageKey,
    type QuickJournalThemeSelectionName,
} from "./View/QuickJournalTheme";
import { ShowRecentJournalEntries } from "./View/RecentEntries/ShowRecentJournalEntries";

export function QuickJournal({
    keyboardOffsetY = 0,
    themeName,
    onKeyboardFocusChange,
    onRecentEntriesChange,
    onEntrySubmitted,
}: {
    keyboardOffsetY?: number;
    themeName?: QuickJournalThemeSelectionName;
    onKeyboardFocusChange?: (hasKeyboardFocus: boolean) => void;
    onRecentEntriesChange?: (
        recentEntries: IQuickJournalState["recentEntries"],
        weeklyHourlyCounts: Record<string, number>
    ) => void;
    onEntrySubmitted?: () => void;
}) {
    const {
        state,
        setState,
        shouldFadeInHistoricalRows,
    } = useQuickJournalDataController();
    const { visualViewportScale } = useQuickJournalViewportController();
    const activeThemeName = themeName ?? LoadStoredQuickJournalThemeName();
    const quickJournalTheme = GetQuickJournalTheme(activeThemeName);

    React.useEffect(() => {
        return () => onKeyboardFocusChange?.(false);
    }, [onKeyboardFocusChange]);

    React.useEffect(() => {
        onRecentEntriesChange?.(
            state.recentEntries,
            state.weeklyHourlyCounts
        );
    }, [onRecentEntriesChange, state.recentEntries, state.weeklyHourlyCounts]);

    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        window.localStorage.setItem(
            quickJournalThemeStorageKey,
            activeThemeName
        );
    }, [activeThemeName]);

    const journalScale =
        IsIosDevice() && visualViewportScale > 0
            ? 1 / visualViewportScale
            : 1;

    return (
        <div
            {...Style(quickJournalTheme.containerClassName, {
                background: `transparent`,
                position: `relative`,
                marginBottom: `0px`,
                overflow: `visible`,
            })}
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                width: "100%",
                height: "100%",
                transform:
                    keyboardOffsetY !== 0 && IsIosDevice()
                        ? `translateY(${keyboardOffsetY}px) scale(${journalScale})`
                        : `translateY(0) scale(${journalScale})`,
                transformOrigin: "left bottom",
                transition: `transform 0.15s ease-out`,
            }}
        >
            <div
                style={{
                    position: "relative",
                    zIndex: 1,
                    width: "100%",
                }}
            >
                <ShowRecentJournalEntries
                    state={state}
                    shouldFadeInHistoricalRows={shouldFadeInHistoricalRows}
                    theme={quickJournalTheme}
                />
                <JournalCaptureTextArea
                    id="quickJournal_target"
                    autoFocusOnMount
                    onChange={(draft: string) => {
                        SetState<IQuickJournalState>(
                            ESharedFunction.TypeAwayInputBox_state,
                            { draft }
                        );
                    }}
                    value={state.draft}
                    classes={textAreaStyleClass}
                    dynamicOpacity
                    theme={quickJournalTheme}
                    state={state}
                    setState={setState}
                    onEnter={() => onEntrySubmitted?.()}
                    onFocusChange={(hasFocus: boolean) => {
                        onKeyboardFocusChange?.(hasFocus);
                    }}
                />
            </div>
        </div>
    );
}


const textAreaStyleClass = StyleClass(`quickJournalInputBox`, {
    ":focus": {
        boxShadow: "none",
    },
});
